import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { MessageService, TreeNode } from 'primeng/api';
import { TreeModule } from 'primeng/tree';
import { BusinessUnitService } from
  '@app/api/user-service/api/businessUnit.service';
import { BusinessUnitTreeNode } from
  '@app/api/user-service/model/businessUnitTreeNode';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

@Component({
  selector: 'app-business-unit-tree',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TreeModule,
    TranslateModule,
    SearchFormComponent,
    AppButtonComponent,
  ],
  templateUrl: './business-unit-tree.component.html',
  styleUrl: './business-unit-tree.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessUnitTreeComponent implements OnInit, OnChanges {
  @Input() units: BusinessUnitTreeNode[] = [];
  @Input() loading = false;
  @Input() selectedUnitId: string | null = null;
  @Input() expandedUnits = new Set<string>();
  @Input() canUpdate = false;

  @Output() unitSelect = new EventEmitter<BusinessUnitTreeNode>();
  @Output() expandedUnitsChange = new EventEmitter<Set<string>>();
  @Output() assignStore = new EventEmitter<void>();
  @Output() unlinkChannel = new EventEmitter<BusinessUnitTreeNode>();
  @Output() syncDone = new EventEmitter<void>();

  readonly treeData = signal<TreeNode[]>([]);
  readonly filteredTreeData = signal<TreeNode[]>([]);
  readonly selectedNode = signal<TreeNode | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly syncing = signal(false);

  private readonly destroyRef = inject(DestroyRef);
  private readonly businessUnitService = inject(BusinessUnitService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  readonly searchConfig: FormFieldConfig[] = [
    {
      key: 'name',
      label: 'configuration.business_unit.search.business_unit_name',
      type: 'text',
      width: '100%',
    },
  ];

  searchKeyword = '';

  ngOnInit(): void {
    this.isLoading.set(this.loading);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']) {
      this.isLoading.set(changes['loading'].currentValue);
    }

    if (
      changes['units'] ||
      changes['expandedUnits'] ||
      changes['selectedUnitId']
    ) {
      this.rebuildTree();
    }
  }

  /**
   * Handle search form submission
   */
  onSearch(formData: SearchFormData): void {
    const searchText = (formData['name'] || '').toString().trim();
    this.searchKeyword = searchText;
    this.filterTreeData(searchText);
  }

  /**
   * Handle search form reset
   */
  onReset(): void {
    this.searchKeyword = '';
    this.filterTreeData('');
  }

  onNodeSelect(node: TreeNode): void {
    const unit = node.data as BusinessUnitTreeNode | undefined;

    if (unit) {
      this.unitSelect.emit(unit);
    }
  }

  onNodeExpand(node: TreeNode): void {
    this.updateExpandedUnits(node.key, true);
  }

  onNodeCollapse(node: TreeNode): void {
    this.updateExpandedUnits(node.key, false);
  }

  onAssignStore(): void {
    this.assignStore.emit();
  }

  onSyncDepartments(): void {
    this.syncing.set(true);
    this.businessUnitService
      .syncOdooDepartments('body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        finalize(() => this.syncing.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.syncDone.emit();
        },
        error: (err: unknown) => {
          console.error(
            '[BusinessUnitTreeComponent] Sync departments failed:',
            err,
          );
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant(
              'configuration.business_unit.sidebar.sync_departments_failed',
            ),
          });
        },
      });
  }

  onUnlinkChannel(node: TreeNode, event: Event): void {
    event.stopPropagation();
    const unit = node.data as BusinessUnitTreeNode | undefined;
    if (unit?.externalSystem === 'CHANNEL' && unit?.id != null) {
      this.unlinkChannel.emit(unit);
    }
  }

  isChannelNode(node: TreeNode): boolean {
    const data = node?.data as BusinessUnitTreeNode | undefined;
    return data?.externalSystem === 'CHANNEL';
  }


  private rebuildTree(): void {
    const treeNodes = this.units.map((unit) => this.mapUnitToTreeNode(unit));

    this.treeData.set(treeNodes);
    this.filterTreeData(this.searchKeyword);
    this.syncSelectedNode();
  }

  /**
   * Filter tree data based on search text
   */
  private filterTreeData(searchText: string): void {
    if (!searchText.trim()) {
      this.filteredTreeData.set(this.treeData());
      return;
    }

    const filtered: TreeNode[] = [];
    this.treeData().forEach((node) => {
      const matchesSearch = node.label?.toLowerCase().includes(searchText.toLowerCase());
      const children = node.children
        ? this.filterNodes(node.children, searchText)
        : [];

      if (matchesSearch || children.length > 0) {
        filtered.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
    });

    this.filteredTreeData.set(filtered);
  }

  /**
   * Recursively filter tree nodes
   */
  private filterNodes(nodes: TreeNode[], searchText: string): TreeNode[] {
    const filtered: TreeNode[] = [];

    nodes.forEach((node) => {
      const matchesSearch = node.label
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      const children = node.children
        ? this.filterNodes(node.children, searchText)
        : [];

      // Include node if it matches search OR if any of its descendants match
      if (matchesSearch || children.length > 0) {
        filtered.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
    });

    return filtered;
  }

  private syncSelectedNode(): void {
    if (!this.selectedUnitId) {
      this.selectedNode.set(null);
      return;
    }

    const selectedNode = this.findNodeByKey(
      this.filteredTreeData(),
      this.selectedUnitId,
    );

    this.selectedNode.set(selectedNode);
  }

  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }

      if (!node.children?.length) {
        continue;
      }

      const match = this.findNodeByKey(node.children, key);
      if (match) {
        return match;
      }
    }

    return null;
  }

  private mapUnitToTreeNode(unit: BusinessUnitTreeNode): TreeNode {
    const key = this.getNodeKey(unit);
    const children = this.getChildren(unit).map((child) =>
      this.mapUnitToTreeNode(child),
    );

    return {
      key,
      label: unit.name || unit.code || `Unit ${key}`,
      data: unit,
      expanded: this.expandedUnits.has(key),
      children: children.length > 0 ? children : undefined,
    };
  }

  private getNodeKey(unit: BusinessUnitTreeNode): string {
    return String(unit.id ?? unit.code ?? unit.name ?? '');
  }

  private getChildren(unit: BusinessUnitTreeNode): BusinessUnitTreeNode[] {
    const rawUnit = unit as Record<string, unknown>;
    const childKeys = ['children', 'subUnits', 'businessUnits'];

    for (const key of childKeys) {
      const children = rawUnit[key];
      if (Array.isArray(children)) {
        return children as BusinessUnitTreeNode[];
      }
    }

    return [];
  }

  private updateExpandedUnits(
    key: string | undefined,
    expanded: boolean,
  ): void {
    if (!key) {
      return;
    }

    const nextExpandedUnits = new Set(this.expandedUnits);

    if (expanded) {
      nextExpandedUnits.add(key);
    } else {
      nextExpandedUnits.delete(key);
    }

    this.expandedUnitsChange.emit(nextExpandedUnits);
  }
}
